import { Spin } from 'antd';
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { getDevIdentity, INTENDED_ROUTE_KEY, isDevLoginEnabled } from './devLogin';
import { useSession } from './useSession';
import { useSessionTimer } from './useSessionTimer';

type Props = {
  children: ReactNode;
};

/**
 * Guards a subtree by requiring a valid server-side session. Unauthenticated
 * requests are sent to the BFF Entra SSO login at GET /auth/login.
 *
 * In Dev Login mode (localhost + Dev only) session checks are bypassed and the user
 * is sent to the role-picker screen if no identity has been picked yet.
 */
export function AuthGuard({ children }: Props) {
  const location = useLocation();
  const devLogin = isDevLoginEnabled();
  const { user, loading } = useSession();

  // Mounted unconditionally (before any early return below) per Rules of Hooks; the
  // hook itself no-ops until `user` resolves. AuthGuard wraps every protected route, so
  // this is the single natural place to run the session-refresh timer for the whole app.
  useSessionTimer(
    user
      ? {
          accessTokenExpiresAt: user.accessTokenExpiresAt,
          sessionExpiresAt: user.sessionExpiresAt,
          refreshIntervalSeconds: user.refreshIntervalSeconds,
        }
      : null,
  );

  if (devLogin) {
    if (!getDevIdentity()) {
      sessionStorage.setItem(INTENDED_ROUTE_KEY, location.pathname + location.search);
      return <Navigate to="/auth/dev-login" replace />;
    }
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    window.location.href = `/auth/login?returnUrl=${returnUrl}`;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
}
